import axios from 'axios';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Stack,
  Button,
  Tooltip,
  Slider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as FitToScreenIcon,
  GridOn as GridIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RotateRight as RotateIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  TextFields as TextIcon
} from '@mui/icons-material';

const MoodboardCanvas = ({
  images = [],
  onBack,
  brandId = '',
  selectedBimester = '',
  bimesterLabel = '',
  committedLooks = [],
  kamSession = {},
}) => {  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(0.5); // Start at 50% to see full canvas
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [canvasItems, setCanvasItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [lookZones, setLookZones] = useState([]);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [selectedLookForZone, setSelectedLookForZone] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [isDraggingZone, setIsDraggingZone] = useState(false);
  const [zoneDragStart, setZoneDragStart] = useState({ x: 0, y: 0 });
  const [isResizingZone, setIsResizingZone] = useState(false);
  const [zoneResizeStart, setZoneResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [renameZoneDialog, setRenameZoneDialog] = useState(false);
  const [zoneNewName, setZoneNewName] = useState('');
  const [editingTextId, setEditingTextId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      sender: 'kam',
      text: "Hi! I'm Kam, your Assistant Designer. I can help you organize your mood board, suggest compositions, and provide feedback on your look zones. How can I assist you?",
      timestamp: new Date()
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [saveDialog, setSaveDialog] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
  const [lastSaved, setLastSaved] = useState(null);

  // Canvas dimensions
  const CANVAS_WIDTH = 3000;
  const CANVAS_HEIGHT = 2000;

  // Auto-save canvas state to localStorage (per bimester)
  useEffect(() => {
    if (!selectedBimester) return;

    const saveToLocalStorage = () => {
      const canvasState = {
        canvasItems,
        lookZones,
        zoom,
        pan,
        timestamp: new Date().toISOString()
      };
      const storageKey = `moodboardCanvas_${selectedBimester}`;
      localStorage.setItem(storageKey, JSON.stringify(canvasState));
    };

    const timeoutId = setTimeout(saveToLocalStorage, 1000); // Debounce saves
    return () => clearTimeout(timeoutId);
  }, [canvasItems, lookZones, zoom, pan, selectedBimester]);

  // Load canvas state on mount or when bimester changes
  useEffect(() => {
    if (!selectedBimester) return;

    const loadCanvasState = async () => {
      // Try to load from API first if brandId is available
      if (brandId) {
        try {
          const response = await axios.get(`/api/moodboard/load/${brandId}/${selectedBimester}`);
          if (response.data?.success && response.data?.canvasState) {
            const { canvasItems: savedItems, lookZones: savedZones, zoom: savedZoom, pan: savedPan, timestamp } = response.data.canvasState;
            if (savedItems) setCanvasItems(savedItems);
            if (savedZones) setLookZones(savedZones);
            if (savedZoom) setZoom(savedZoom);
            if (savedPan) setPan(savedPan);
            if (timestamp) setLastSaved(new Date(timestamp));
            return; // Successfully loaded from API
          }
        } catch (error) {
          console.error('Error loading from API, falling back to localStorage:', error);
        }
      }

      // Fallback to localStorage
      const storageKey = `moodboardCanvas_${selectedBimester}`;
      const savedState = localStorage.getItem(storageKey);
      if (savedState) {
        try {
          const { canvasItems: savedItems, lookZones: savedZones, zoom: savedZoom, pan: savedPan, timestamp } = JSON.parse(savedState);
          if (savedItems) setCanvasItems(savedItems);
          if (savedZones) setLookZones(savedZones);
          if (savedZoom) setZoom(savedZoom);
          if (savedPan) setPan(savedPan);
          if (timestamp) setLastSaved(new Date(timestamp));
        } catch (error) {
          console.error('Error loading saved canvas from localStorage:', error);
        }
      } else {
        // Reset canvas when switching to a bimester with no saved state
        setCanvasItems([]);
        setLookZones([]);
        setZoom(0.5);
        setPan({ x: 0, y: 0 });
        setLastSaved(null);
      }
    };

    loadCanvasState();
  }, [brandId, selectedBimester]);

  // Zoom controls
  const handleZoomIn = () => setZoom(Math.min(zoom + 0.1, 2));
  const handleZoomOut = () => setZoom(Math.max(zoom - 0.1, 0.1));
  const handleFitToScreen = () => {
    setZoom(0.5);
    setPan({ x: 0, y: 0 });
  };

  // Pan controls
  const handleMouseDown = (e) => {
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) { // Middle mouse or Shift+Click
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    } else if (isDragging && selectedItem) {
      // Drag canvas item
      const rect = canvasRef.current.getBoundingClientRect();
      const newX = (e.clientX - rect.left - pan.x) / zoom - dragStart.x;
      const newY = (e.clientY - rect.top - pan.y) / zoom - dragStart.y;

      setCanvasItems(canvasItems.map(item =>
        item.id === selectedItem ? { ...item, x: newX, y: newY } : item
      ));
    } else if (isDraggingZone && selectedZone) {
      // Drag look zone
      const rect = canvasRef.current.getBoundingClientRect();
      const newX = (e.clientX - rect.left - pan.x) / zoom - zoneDragStart.x;
      const newY = (e.clientY - rect.top - pan.y) / zoom - zoneDragStart.y;

      setLookZones(lookZones.map(zone =>
        zone.id === selectedZone ? { ...zone, x: newX, y: newY } : zone
      ));
    } else if (isResizingZone && selectedZone) {
      // Resize look zone
      const rect = canvasRef.current.getBoundingClientRect();
      const deltaX = ((e.clientX - rect.left - pan.x) / zoom) - zoneResizeStart.x;
      const deltaY = ((e.clientY - rect.top - pan.y) / zoom) - zoneResizeStart.y;

      const newWidth = Math.max(100, zoneResizeStart.width + deltaX);
      const newHeight = Math.max(100, zoneResizeStart.height + deltaY);

      setLookZones(lookZones.map(zone =>
        zone.id === selectedZone ? { ...zone, width: newWidth, height: newHeight } : zone
      ));
    } else if (isResizing && selectedItem) {
      // Resize canvas item
      const rect = canvasRef.current.getBoundingClientRect();
      const deltaX = ((e.clientX - rect.left - pan.x) / zoom) - resizeStart.x;
      const deltaY = ((e.clientY - rect.top - pan.y) / zoom) - resizeStart.y;

      const newWidth = Math.max(50, resizeStart.width + deltaX);
      const newHeight = Math.max(50, resizeStart.height + deltaY);

      setCanvasItems(canvasItems.map(item =>
        item.id === selectedItem ? { ...item, width: newWidth, height: newHeight } : item
      ));
    }
  };

  // Zone drag handlers
  const handleZoneMouseDown = (e, zone) => {
    e.stopPropagation();
    if (e.button === 0 && !e.shiftKey) {
      setSelectedZone(zone.id);
      setSelectedItem(null); // Deselect any canvas item
      setIsDraggingZone(true);
      const rect = canvasRef.current.getBoundingClientRect();
      const clickX = (e.clientX - rect.left - pan.x) / zoom;
      const clickY = (e.clientY - rect.top - pan.y) / zoom;
      setZoneDragStart({
        x: clickX - zone.x,
        y: clickY - zone.y
      });
    }
  };

  const handleDeleteZone = (zoneId) => {
    setLookZones(lookZones.filter(zone => zone.id !== zoneId));
    setSelectedZone(null);
  };

  const handleOpenRenameZone = (zone) => {
    setZoneNewName(zone.name);
    setRenameZoneDialog(true);
  };

  const handleRenameZone = () => {
    if (selectedZone && zoneNewName.trim()) {
      setLookZones(lookZones.map(zone =>
        zone.id === selectedZone ? { ...zone, name: zoneNewName.trim() } : zone
      ));
      setRenameZoneDialog(false);
      setZoneNewName('');
    }
  };

  // Double-click to add text at position
  const handleCanvasDoubleClick = (e) => {
    // Only if clicking on the canvas background, not on items
    if (e.target === e.currentTarget || e.target.closest('[data-canvas-bg]')) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;

      const newTextItem = {
        id: Date.now(),
        type: 'text',
        content: 'Double-click to edit',
        x,
        y,
        width: 200,
        height: 50,
        fontSize: 16,
        color: '#000000'
      };

      setCanvasItems([...canvasItems, newTextItem]);
      setEditingTextId(newTextItem.id);
      setSelectedItem(newTextItem.id);
    }
  };

  // Update text content
  const handleTextChange = (id, newContent) => {
    setCanvasItems(canvasItems.map(item =>
      item.id === id ? { ...item, content: newContent } : item
    ));
  };

  // Finish editing text
  const handleTextBlur = (id) => {
    setEditingTextId(null);
    // Remove empty text items
    const item = canvasItems.find(i => i.id === id);
    if (item && item.type === 'text' && !item.content.trim()) {
      setCanvasItems(canvasItems.filter(i => i.id !== id));
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    setIsDragging(false);
    setIsDraggingZone(false);
    setIsResizing(false);
    setIsResizingZone(false);
  };

  // Zone resize handler
  const handleZoneResizeMouseDown = (e, zone) => {
    e.stopPropagation();
    setIsResizingZone(true);
    setSelectedZone(zone.id);
    const rect = canvasRef.current.getBoundingClientRect();
    setZoneResizeStart({
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
      width: zone.width,
      height: zone.height
    });
  };

  // Add image to canvas
  const handleImageDrop = useCallback((image, position) => {
    const newItem = {
      id: Date.now() + Math.random(),
      imageUrl: image.url,
      imageName: image.name,
      x: position.x,
      y: position.y,
      width: 300,
      height: 300,
      rotation: 0,
      zIndex: canvasItems.length
    };
    setCanvasItems([...canvasItems, newItem]);
  }, [canvasItems]);

  // Handle drag from foyer
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const imageData = e.dataTransfer.getData('image');
    if (imageData) {
      const image = JSON.parse(imageData);
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;
      handleImageDrop(image, { x, y });
    }
  };

  // Image manipulation
  const handleItemClick = (item) => {
    setSelectedItem(item.id);
  };

  const handleItemMouseDown = (e, item) => {
    e.stopPropagation();
    if (e.button === 0 && !e.shiftKey) {
      setSelectedItem(item.id);
      setIsDragging(true);
      const rect = canvasRef.current.getBoundingClientRect();
      const clickX = (e.clientX - rect.left - pan.x) / zoom;
      const clickY = (e.clientY - rect.top - pan.y) / zoom;
      setDragStart({
        x: clickX - item.x,
        y: clickY - item.y
      });
    }
  };

  const handleResizeMouseDown = (e, item) => {
    e.stopPropagation();
    setSelectedItem(item.id);
    setIsResizing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left - pan.x) / zoom;
    const clickY = (e.clientY - rect.top - pan.y) / zoom;
    setResizeStart({
      x: clickX,
      y: clickY,
      width: item.width,
      height: item.height
    });
  };

  const handleDeleteItem = () => {
    if (selectedItem) {
      setCanvasItems(canvasItems.filter(item => item.id !== selectedItem));
      setSelectedItem(null);
    }
  };

  const handleRotateItem = () => {
    if (selectedItem) {
      setCanvasItems(canvasItems.map(item =>
        item.id === selectedItem ? { ...item, rotation: item.rotation + 90 } : item
      ));
    }
  };

  const handleResizeItem = (itemId, newWidth, newHeight) => {
    setCanvasItems(canvasItems.map(item =>
      item.id === itemId ? { ...item, width: newWidth, height: newHeight } : item
    ));
  };

  // Add look zone
  const handleAddZone = () => {
    if (selectedLookForZone) {
      const look = committedLooks.find(l => l.id === selectedLookForZone);
      if (look) {
        const newZone = {
          id: Date.now(),
          lookId: look.id,
          name: look.tabLabel || look.categoryLabel || `Look ${look.sequence}`,
          x: 100 + (lookZones.length * 50),
          y: 100 + (lookZones.length * 50),
          width: 800,
          height: 600,
          color: `hsl(${Math.random() * 360}, 70%, 60%)`
        };
        setLookZones([...lookZones, newZone]);
        setSelectedLookForZone('');
        setZoneDialog(false);
      }
    }
  };

  // Save canvas to server
  const handleSaveCanvas = async () => {
    setSaveStatus('saving');
    try {
      const canvasState = {
        canvasItems,
        lookZones,
        zoom,
        pan,
        timestamp: new Date().toISOString()
      };

      // Save to API if brandId is available
      if (brandId) {
        // Prepare foyer images for saving (exclude file objects)
        const foyerImages = images.map(img => ({
          id: img.id,
          url: img.url,
          name: img.name,
          type: img.type,
          source: img.source,
          sourceUrl: img.sourceUrl
        }));

        const response = await axios.post('/api/moodboard/save', {
          brandId,
          bimester: selectedBimester,
          canvasState,
          foyerImages
        });

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Failed to save moodboard');
        }
      }

      // Save to localStorage as backup (per bimester)
      const storageKey = `moodboardCanvas_${selectedBimester}`;
      localStorage.setItem(storageKey, JSON.stringify(canvasState));
      setLastSaved(new Date());
      setSaveStatus('saved');

      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving canvas:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Export canvas as image
  const handleExportCanvas = async () => {
    try {
      // Use html2canvas library for export (would need to install)
      // For now, we'll create a simple JSON export
      const canvasData = {
        canvasItems,
        lookZones,
        dimensions: { width: CANVAS_WIDTH, height: CANVAS_HEIGHT },
        exportedAt: new Date().toISOString()
      };

      const dataStr = JSON.stringify(canvasData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `moodboard-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error exporting canvas:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Clear canvas
  const handleClearCanvas = () => {
    if (window.confirm(`Are you sure you want to clear the canvas for ${bimesterLabel}? This action cannot be undone.`)) {
      setCanvasItems([]);
      setLookZones([]);
      setSelectedItem(null);
      const storageKey = `moodboardCanvas_${selectedBimester}`;
      localStorage.removeItem(storageKey);
    }
  };

  // Chat functionality
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
  
    const userText = chatInput.trim();
  
    const newMessage = {
      id: Date.now(),
      sender: 'user',
      text: userText,
      timestamp: new Date()
    };
  
    setChatMessages((prev) => [...prev, newMessage]);
    setChatInput('');
  
    try {
      // Summarize current moodboard layout for Kam
      const zoneSummary =
        lookZones && lookZones.length
          ? lookZones
              .map((z) => `${z.lookName || z.name || 'Unnamed look'} @ (${Math.round(z.x)}, ${Math.round(z.y)})`)
              .join(' | ')
          : 'No look zones defined yet.';
  
      const canvasSummary = `Canvas has ${canvasItems.length} image tiles and ${lookZones.length} look zones. Zones: ${zoneSummary}`;
  
      const context = {
        page: 'moodboard',
        brandId: brandId || '',
        bimester: selectedBimester || '',
        cycleLabel: kamSession.cycleLabel || bimesterLabel || '',
        collectionName: kamSession.collectionName || '',
        collectionTheme: kamSession.collectionTheme || '',
        sessionId: kamSession.sessionId || undefined,
        source: 'moodboard-canvas'
      };
  
      const response = await axios.post('/api/assistant/design', {
        messages: [
          {
            role: 'system',
            content:
              "You are Kam, an assistant designer helping refine a fashion moodboard canvas. Use the moodboard context to give specific, practical feedback on composition, clustering, look zones, and collection storytelling. Keep responses concise and actionable."
          },
          {
            role: 'user',
            content: `${userText}\n\nMoodboard context:\n- Bimester: ${selectedBimester || 'N/A'} (${bimesterLabel || 'N/A'})\n- Collection: ${kamSession.collectionName || 'Unnamed'}\n- Theme: ${kamSession.collectionTheme || 'Not specified'}\n- ${canvasSummary}`
          }
        ],
        context
      });
  
      const kamText =
        response?.data?.message ||
        'I had trouble reading the response from the assistant. Please try again.';
  
      const kamMessage = {
        id: Date.now() + 1,
        sender: 'kam',
        text: kamText,
        timestamp: new Date()
      };
  
      setChatMessages((prev) => [...prev, kamMessage]);
    } catch (error) {
      console.error('Kam moodboard chat error:', error);
      const fallbackText =
        error?.response?.data?.message ||
        'I ran into an issue trying to analyze this moodboard. You can try again, or tell me what you want to focus on (color story, silhouettes, or look zoning).';
  
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'kam',
        text: fallbackText,
        timestamp: new Date()
      };
  
      setChatMessages((prev) => [...prev, errorMessage]);
    }
  };

  const generateKamResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    if (lowerMessage.includes('zone') || lowerMessage.includes('section')) {
      return "Great question! Look zones help organize your mood board by garment category or collection looks. Try creating zones for each look, then drag relevant images into them. This keeps your composition organized and easier to review.";
    } else if (lowerMessage.includes('composition') || lowerMessage.includes('arrange')) {
      return "For a strong composition, try grouping similar items together and creating visual hierarchy with size and placement. Use the look zones to define clear sections, and don't be afraid to overlap images slightly for a more dynamic feel.";
    } else if (lowerMessage.includes('color') || lowerMessage.includes('palette')) {
      return "Color coordination is key! Consider grouping images by color family within each look zone. This helps buyers visualize the color story for each look and makes your mood board more cohesive.";
    } else if (lowerMessage.includes('save') || lowerMessage.includes('export')) {
      return "You can save your canvas state at any time using the Save Canvas button. In a future update, you'll be able to export your mood board as a high-resolution image for presentations.";
    } else {
      return `I'm here to help with your mood board! You currently have ${canvasItems.length} images on the canvas and ${lookZones.length} look zones. What would you like to know about organizing your collection?`;
    }
  };

  const quickSuggestions = [
    "How should I organize my zones?",
    "Tips for composition?",
    "How do I save my work?"
  ];

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1300,
      bgcolor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'row',
      gap: 2
    }}>
      {/* Canvas Sidebar */}
      <Paper sx={{ width: 280, flexShrink: 0, p: 2, borderRadius: 0, overflowY: 'auto', height: '100vh' }}>
        <Stack spacing={3}>
          {/* Image Library */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              📚 Image Library
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Drag images onto the canvas
            </Typography>
            <Stack spacing={1}>
              {images.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No images in foyer
                </Typography>
              ) : (
                images.map((image) => (
                  <Box
                    key={image.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('image', JSON.stringify(image));
                    }}
                    sx={{
                      width: '100%',
                      height: 80,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'grab',
                      border: '2px solid transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        border: '2px solid #3b82f6',
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
                      }
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          {/* Layers Panel */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              📋 Layers ({canvasItems.length})
            </Typography>
            <Stack spacing={0.5}>
              {canvasItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No items on canvas
                </Typography>
              ) : (
                canvasItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedItem === item.id ? 'primary.main' : 'transparent',
                      color: selectedItem === item.id ? 'white' : 'text.primary',
                      '&:hover': {
                        bgcolor: selectedItem === item.id ? 'primary.dark' : 'action.hover'
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 0.5,
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                      >
                        <img
                          src={item.imageUrl}
                          alt={item.imageName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {item.imageName}
                        </Typography>
                        <Typography variant="caption" color={selectedItem === item.id ? 'white' : 'text.secondary'}>
                          {Math.round(item.width)}×{Math.round(item.height)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          {/* Look Zones */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                🎯 Look Zones ({lookZones.length})
              </Typography>
              <IconButton size="small" onClick={() => setZoneDialog(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack spacing={0.5}>
              {lookZones.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No zones created
                </Typography>
              ) : (
                lookZones.map((zone) => (
                  <Box
                    key={zone.id}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: `2px solid ${zone.color}`,
                      bgcolor: `${zone.color}15`
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {zone.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          {/* Canvas Stats */}
          <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Canvas: {CANVAS_WIDTH}×{CANVAS_HEIGHT}px
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Main Canvas Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 0, borderRadius: 0, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" onClick={onBack}>
              ← Back to Foyer
            </Button>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6">Mood Board Canvas</Typography>
              {bimesterLabel && (
                <Chip
                  label={bimesterLabel}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Zoom controls */}
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} size="small">
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} size="small">
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fit to Screen">
              <IconButton onClick={handleFitToScreen} size="small">
                <FitToScreenIcon />
              </IconButton>
            </Tooltip>

            {/* Grid toggle */}
            <Tooltip title="Toggle Grid">
              <IconButton onClick={() => setShowGrid(!showGrid)} size="small" color={showGrid ? 'primary' : 'default'}>
                <GridIcon />
              </IconButton>
            </Tooltip>

            {/* Add zone */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setZoneDialog(true)}
              size="small"
            >
              Add Look Zone
            </Button>

            {/* Image controls (when selected) */}
            {selectedItem && (
              <>
                <Tooltip title="Rotate">
                  <IconButton onClick={handleRotateItem} size="small" color="primary">
                    <RotateIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={handleDeleteItem} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Save Options */}
            <Tooltip title={lastSaved ? `Last saved: ${lastSaved.toLocaleTimeString()}` : 'Save canvas'}>
              <Button
                variant="contained"
                startIcon={saveStatus === 'saving' ? <CloudUploadIcon /> : <SaveIcon />}
                onClick={handleSaveCanvas}
                disabled={saveStatus === 'saving'}
                color={saveStatus === 'saved' ? 'success' : 'primary'}
              >
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Save'}
              </Button>
            </Tooltip>

            <Tooltip title="Export as JSON">
              <IconButton onClick={handleExportCanvas} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>

            {/* Kam Chat Toggle */}
            <Tooltip title="Chat with Kam">
              <IconButton
                onClick={() => setChatOpen(!chatOpen)}
                size="small"
                color={chatOpen ? 'primary' : 'default'}
                sx={{
                  bgcolor: chatOpen ? 'primary.main' : 'transparent',
                  color: chatOpen ? 'white' : 'inherit',
                  '&:hover': {
                    bgcolor: chatOpen ? 'primary.dark' : 'action.hover'
                  }
                }}
              >
                <ChatIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Canvas Area */}
      <Box
        sx={{
          flex: 1,
          bgcolor: '#2a2a2a',
          borderRadius: 0,
          overflow: 'hidden',
          position: 'relative',
          cursor: isPanning ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Canvas content */}
        <Box
          ref={canvasRef}
          data-canvas-bg
          onDoubleClick={handleCanvasDoubleClick}
          sx={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            bgcolor: '#ffffff',
            position: 'absolute',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            backgroundImage: showGrid ? 'linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)' : 'none',
            backgroundSize: showGrid ? '50px 50px' : 'auto',
            boxShadow: '0 0 50px rgba(0,0,0,0.3)'
          }}
        >
          {/* Look Zones */}
          {lookZones.map(zone => (
            <Box
              key={zone.id}
              onMouseDown={(e) => handleZoneMouseDown(e, zone)}
              sx={{
                position: 'absolute',
                left: zone.x,
                top: zone.y,
                width: zone.width,
                height: zone.height,
                border: selectedZone === zone.id ? `4px solid ${zone.color}` : `3px dashed ${zone.color}`,
                borderRadius: 2,
                bgcolor: `${zone.color}15`,
                cursor: isDraggingZone && selectedZone === zone.id ? 'grabbing' : 'grab',
                boxShadow: selectedZone === zone.id ? `0 0 20px ${zone.color}50` : 'none',
                transition: isDraggingZone ? 'none' : 'border 0.2s, box-shadow 0.2s'
              }}
            >
              <Chip
                label={zone.name}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: zone.color,
                  color: '#fff',
                  fontWeight: 600
                }}
              />
              {/* Action buttons for selected zone */}
              {selectedZone === zone.id && (
                <>
                  <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 4, right: 4 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRenameZone(zone);
                      }}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        width: 24,
                        height: 24,
                        '&:hover': {
                          bgcolor: 'primary.dark'
                        }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteZone(zone.id);
                      }}
                      sx={{
                        bgcolor: 'error.main',
                        color: 'white',
                        width: 24,
                        height: 24,
                        '&:hover': {
                          bgcolor: 'error.dark'
                        }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                  {/* Resize Handle */}
                  <Box
                    onMouseDown={(e) => handleZoneResizeMouseDown(e, zone)}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 20,
                      height: 20,
                      cursor: 'nwse-resize',
                      bgcolor: zone.color,
                      borderRadius: '4px 0 4px 0',
                      '&:hover': {
                        bgcolor: zone.color,
                        filter: 'brightness(0.8)'
                      }
                    }}
                  />
                </>
              )}
            </Box>
          ))}

          {/* Canvas Items */}
          {canvasItems.map(item => (
            <Box
              key={item.id}
              onClick={() => handleItemClick(item)}
              onMouseDown={(e) => handleItemMouseDown(e, item)}
              onDoubleClick={(e) => {
                if (item.type === 'text') {
                  e.stopPropagation();
                  setEditingTextId(item.id);
                }
              }}
              sx={{
                position: 'absolute',
                left: item.x,
                top: item.y,
                width: item.width,
                height: item.type === 'text' ? 'auto' : item.height,
                minHeight: item.type === 'text' ? 30 : item.height,
                transform: `rotate(${item.rotation || 0}deg)`,
                cursor: isDragging && selectedItem === item.id ? 'grabbing' : 'grab',
                border: selectedItem === item.id ? '3px solid #3b82f6' : item.type === 'text' ? '1px dashed #ccc' : '2px solid transparent',
                boxShadow: selectedItem === item.id ? '0 0 20px rgba(59, 130, 246, 0.5)' : item.type === 'text' ? 'none' : '0 4px 8px rgba(0,0,0,0.2)',
                transition: isDragging ? 'none' : 'border 0.2s, box-shadow 0.2s',
                zIndex: item.zIndex || 1,
                bgcolor: item.type === 'text' ? 'transparent' : 'transparent',
                '&:hover': {
                  border: '3px solid #3b82f6'
                }
              }}
            >
              {item.type === 'text' ? (
                editingTextId === item.id ? (
                  <textarea
                    autoFocus
                    value={item.content}
                    onChange={(e) => handleTextChange(item.id, e.target.value)}
                    onBlur={() => handleTextBlur(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleTextBlur(item.id);
                      }
                    }}
                    style={{
                      width: '100%',
                      minHeight: '30px',
                      border: 'none',
                      outline: 'none',
                      background: 'rgba(255,255,255,0.9)',
                      padding: '8px',
                      fontSize: `${item.fontSize || 16}px`,
                      fontFamily: 'inherit',
                      resize: 'both',
                      color: item.color || '#000000'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: `${item.fontSize || 16}px`,
                      color: item.color || '#000000',
                      p: 1,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      userSelect: 'none'
                    }}
                  >
                    {item.content || 'Double-click to edit'}
                  </Typography>
                )
              ) : (
                <img
                  src={item.imageUrl}
                  alt={item.imageName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    pointerEvents: 'none'
                  }}
                  draggable={false}
                />
              )}

              {/* Resize Handle */}
              {selectedItem === item.id && (
                <Box
                  onMouseDown={(e) => handleResizeMouseDown(e, item)}
                  sx={{
                    position: 'absolute',
                    bottom: -5,
                    right: -5,
                    width: 20,
                    height: 20,
                    bgcolor: '#3b82f6',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'nwse-resize',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    '&:hover': {
                      bgcolor: '#2563eb',
                      transform: 'scale(1.2)'
                    }
                  }}
                />
              )}
            </Box>
          ))}

          {/* Empty state */}
          {canvasItems.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h5" color="text.secondary" gutterBottom>
                Your canvas is empty
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag images from the foyer to start creating your mood board
              </Typography>
            </Box>
          )}
        </Box>

        {/* Canvas info */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 1,
            fontSize: '0.875rem'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption">
              Canvas: {CANVAS_WIDTH}x{CANVAS_HEIGHT}px
            </Typography>
            <Typography variant="caption">
              Items: {canvasItems.length}
            </Typography>
            <Typography variant="caption">
              Zones: {lookZones.length}
            </Typography>
            {lastSaved && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Auto-saved {new Date(lastSaved).toLocaleTimeString()}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Quick Actions Menu */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            display: 'flex',
            gap: 1
          }}
        >
          <Tooltip title="Clear canvas">
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleClearCanvas}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }}
            >
              Clear Canvas
            </Button>
          </Tooltip>
        </Box>
      </Box>
      </Box>

      {/* Add Zone Dialog */}
      <Dialog open={zoneDialog} onClose={() => setZoneDialog(false)}>
        <DialogTitle>Create Look Zone</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2, minWidth: 300 }}>
            {committedLooks.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Select a look from {bimesterLabel} to create a zone
                </Typography>
                <TextField
                  select
                  label="Select Look"
                  value={selectedLookForZone}
                  onChange={(e) => setSelectedLookForZone(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">
                    <em>Choose a look</em>
                  </MenuItem>
                  {committedLooks
                    .filter(look => !lookZones.some(zone => zone.lookId === look.id))
                    .map((look) => (
                      <MenuItem key={look.id} value={look.id}>
                        {look.tabLabel || look.categoryLabel || `Look ${look.sequence}`}
                      </MenuItem>
                    ))}
                </TextField>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No committed looks available for {bimesterLabel}. Please commit looks in the planning section first.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoneDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddZone}
            variant="contained"
            disabled={!selectedLookForZone}
          >
            Create Zone
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Zone Dialog */}
      <Dialog open={renameZoneDialog} onClose={() => setRenameZoneDialog(false)}>
        <DialogTitle>Rename Look Zone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Zone Name"
            fullWidth
            value={zoneNewName}
            onChange={(e) => setZoneNewName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleRenameZone()}
            sx={{ mt: 2, minWidth: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameZoneDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRenameZone}
            variant="contained"
            disabled={!zoneNewName.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kam Chat Sidebar */}
      {chatOpen && (
        <Paper
          sx={{
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: 380,
            zIndex: 1400,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.5)'
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: '#111928',
              color: 'white',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                component="img"
                src="/Image_Assets/Kam_headshot.png"
                alt="Kam"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(255,255,255,0.2)'
                }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Kam
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Assistant Designer
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setChatOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chat Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: '#f8f9fa',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {chatMessages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                  gap: 1
                }}
              >
                {message.sender === 'kam' && (
                  <Box
                    component="img"
                    src="/Image_Assets/Kam_headshot.png"
                    alt="Kam"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0
                    }}
                  />
                )}
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: '75%',
                    bgcolor: message.sender === 'user' ? '#3b82f6' : 'white',
                    borderRadius: 2,
                    boxShadow: 1
                  }}
                >
                  {message.sender === 'user' ? (
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {message.text}
                    </Typography>
                  ) : (
                    <Box sx={{
                      color: '#1a1a1a',
                      '& h1, & h2, & h3': {
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        mt: 1,
                        mb: 0.5
                      },
                      '& p': {
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        my: 0.5
                      },
                      '& ul, & ol': {
                        pl: 2,
                        my: 0.5,
                        '& li': {
                          fontSize: '0.875rem',
                          lineHeight: 1.4,
                          mb: 0.25
                        }
                      },
                      '& strong': {
                        fontWeight: 600
                      }
                    }}>
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    </Box>
                  )}
                  <Typography variant="caption" sx={{
                    color: message.sender === 'user' ? 'white' : '#666',
                    opacity: 0.8,
                    display: 'block',
                    mt: 0.5,
                    fontSize: '0.7rem'
                  }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Paper>
              </Box>
            ))}

            {/* Quick Suggestions */}
            {chatMessages.length <= 2 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Quick questions:
                </Typography>
                <Stack spacing={0.5}>
                  {quickSuggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      size="small"
                      variant="outlined"
                      onClick={() => setChatInput(suggestion)}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        py: 0.5
                      }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Chat Input */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask Kam about your mood board..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!chatInput.trim()}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  },
                  '&:disabled': {
                    bgcolor: 'action.disabledBackground'
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            </Stack>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MoodboardCanvas;
